import React, { useState } from 'react';
import { bcGovernmentServices, getServicesByCategory, getHighPriorityServices } from '../data/bcGovernmentServices';

const ServicesPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showOnlyHighPriority, setShowOnlyHighPriority] = useState(false);

  const servicesByCategory = getServicesByCategory();
  const categories = Array.from(servicesByCategory.keys());
  const highPriorityServices = getHighPriorityServices();

  const displayedServices = showOnlyHighPriority
    ? highPriorityServices
    : selectedCategory
      ? servicesByCategory.get(selectedCategory) || []
      : bcGovernmentServices;

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      'Identity & Documents': '🆔',
      'Family & Children': '👨‍👩‍👧‍👦',
      'Financial & Tax': '💰',
      'Housing & Transportation': '🏠',
      'Employment & Education': '🎓',
      'Health & Wellness': '🏥',
      'Legal & Social': '⚖️',
      'Emergency & Essential': '🚨',
    };
    return icons[category] || '📋';
  };

  return (
    <div className="max-w-6xl mx-auto">

      {/* Header */}
      <div className="bg-[#003366] text-white p-8 rounded-xl mb-8">
        <h1 className="text-4xl font-bold m-0 mb-2">BC Government Services</h1>
        <p className="text-lg m-0 opacity-90">
          {bcGovernmentServices.length} services available to help you settle in BC
        </p>
      </div>

      {/* Filter Bar */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => { setSelectedCategory(null); setShowOnlyHighPriority(false); }}
            className="flex items-center gap-2 px-6 py-3 border-2 rounded-lg text-base font-medium cursor-pointer transition-colors min-h-[48px]"
            style={{
              backgroundColor: !selectedCategory && !showOnlyHighPriority ? '#003366' : 'white',
              color: !selectedCategory && !showOnlyHighPriority ? 'white' : '#374151',
              borderColor: !selectedCategory && !showOnlyHighPriority ? '#003366' : '#E5E7EB',
            }}
          >
            📋 All Services ({bcGovernmentServices.length})
          </button>

          <button
            onClick={() => { setSelectedCategory(null); setShowOnlyHighPriority(true); }}
            className="flex items-center gap-2 px-6 py-3 border-2 rounded-lg text-base font-medium cursor-pointer transition-colors min-h-[48px]"
            style={{
              backgroundColor: showOnlyHighPriority ? '#003366' : 'white',
              color: showOnlyHighPriority ? 'white' : '#374151',
              borderColor: showOnlyHighPriority ? '#003366' : '#E5E7EB',
            }}
          >
            ⭐ High Priority ({highPriorityServices.length})
          </button>
        </div>

        <div className="flex gap-3 flex-wrap">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => { setSelectedCategory(category); setShowOnlyHighPriority(false); }}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg text-sm cursor-pointer transition-colors min-h-[44px] whitespace-nowrap"
              style={{
                backgroundColor: selectedCategory === category ? '#0066CC' : '#F9FAFB',
                color: selectedCategory === category ? 'white' : '#374151',
                borderColor: selectedCategory === category ? '#0066CC' : '#E5E7EB',
              }}
            >
              <span className="text-xl">{getCategoryIcon(category)}</span>
              <span>{category}</span>
              <span className="opacity-70 text-xs">({servicesByCategory.get(category)?.length})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8 text-center">
        <div className="text-5xl font-bold text-[#003366] mb-2">{displayedServices.length}</div>
        <div className="text-sm text-gray-500">
          {showOnlyHighPriority ? 'High Priority Services' : selectedCategory ? `${selectedCategory} Services` : 'Total Services'}
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {displayedServices.map(service => (
          <div key={service.id} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 flex flex-col hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                {getCategoryIcon(service.category)} {service.category}
              </span>
              {service.priority === 'high' && (
                <span className="px-3 py-1 bg-yellow-300 text-yellow-800 rounded-full text-xs font-bold">
                  ⭐ High Priority
                </span>
              )}
            </div>

            <h3 className="text-lg font-bold text-gray-900 mb-3">{service.name}</h3>
            <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">{service.description}</p>

            <div className="p-3 bg-blue-50 border-l-4 border-blue-400 text-xs text-blue-800 mb-4 rounded-r-lg">
              <strong>Who needs it:</strong> {service.whoNeedsIt}
            </div>

            <div className="flex gap-3 flex-wrap">
              {service.phone && (
                <a href={`tel:${service.phone}`}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white no-underline rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors">
                  📞 {service.phone}
                </a>
              )}
              {service.link && service.link !== '#' && (
                <a href={service.link} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-[#0066CC] text-white no-underline rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
                  🔗 Visit Website
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {displayedServices.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-2xl text-gray-600 mb-2">No services found</h3>
          <p className="text-gray-400">Try selecting a different category</p>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
